import { Injectable, OnDestroy, inject } from '@angular/core';
import { Store } from '@ngrx/store';
import { ReplaySubject } from 'rxjs';
import { filter, pairwise, startWith, takeUntil } from 'rxjs/operators';

import assertDefined from 'app/utils/assert-defined';
import { Loggable } from 'app/utils/logging/loggable';
import { notNil } from 'app/utils/stream-util';
import Social from 'entity/Social';
import SocialTheme from 'entity/SocialTheme';
import SocialThemeType from 'enums/SocialThemeType';

import { selectors } from '../state/app';

import { SceneService } from './scene.service';

@Injectable({
  providedIn: 'root',
})
export class ThemeService extends Loggable implements OnDestroy {
  private sceneService = inject(SceneService);
  private store = inject(Store);

  public theme?: SocialTheme;

  private destroyed$ = new ReplaySubject<boolean>(1);

  public constructor() {
    super();
    this.setDisplayName('ThemeService');
  }

  public init(): void {
    this.destroyed$ = new ReplaySubject(1);
    this.store
      .select(selectors.getCurrentSocial)
      .pipe(
        startWith({} as Social),
        pairwise(),
        filter(([prevRoom, room]) => notNil(room) && notNil(prevRoom) && prevRoom.themeId !== room.themeId),
        takeUntil(this.destroyed$),
      )
      .subscribe(([, room]) => {
        assertDefined(room);
        assertDefined(room.theme);
        this.theme = room.theme;
        this.setTheme(room);
        this.changeMainPlayerTheme(room.theme);
      });
  }

  private setTheme(room: Social): void {
    const theme = room.theme;
    assertDefined(theme);

    let css: Record<string, string | undefined>;

    if (room.theme?.type === SocialThemeType.SINGLE_COLOR) {
      // Room Styling based on theme single color
      css = {
        '--primary-color': theme.colorBackground,
        '--secondary-color': theme.colorSecondary,
        '--button-text-color': theme.colorButtonText,
        '--button-background-color': theme.colorButtonBG,
        '--background-color': theme.colorBackground,
        '--background-gradient': theme.colorBackground,
      };
    } else {
      // Room Styling based on theme layered and single image
      css = {
        '--primary-color': theme.colorPrimary,
        '--secondary-color': theme.colorSecondary,
        '--button-text-color': theme.colorButtonText,
        '--button-background-color': theme.colorButtonBG,
        '--background-color': theme.colorBackground,
        '--background-gradient': theme.colorGradientBackground,
      };
    }

    // Set new variables to dom
    Object.keys(css).forEach((key) => {
      document.body.style.setProperty(key, css[key] ?? null);
      document.body.parentElement?.style.setProperty(key, css[key] ?? null);
    });
  }

  private changeMainPlayerTheme(theme: SocialTheme): void {
    if (!this.sceneService.sceneManager || !this.sceneService.sceneManager.player) {
      return;
    }
    const player = this.sceneService.sceneManager.player;
    void player.updateTheme(theme);
  }

  public ngOnDestroy(): void {
    this.destroyed$.next(true);
    this.destroyed$.complete();
  }
}
